import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import audioManager from "@/lib/audioManager";
import { useAuth } from "@/App";
import { useSubscription } from "@/context/SubscriptionContext";
import { hasAccess, MEMBERSHIP_URL } from "@/lib/subscriptionAccess";
import { fireConfetti } from "@/lib/confetti";
import { 
  Moon, 
  Sparkles, 
  Clock, 
  Wind,
  Heart,
  ChevronRight,
  X,
  Pause,
  Play,
  ChevronLeft,
  Volume2,
  VolumeX,
  Lock
} from "lucide-react";

// Free tier meditation: breath-reset only
const FREE_MEDITATION_IDS = ['breath-reset'];

// Guided text content and audio for each session (Cloudflare R2 URLs)
const GUIDED_SESSIONS = {
  "breath-reset": {
    title: "Breath Reset",
    category: "shorts",
    duration: "3 min",
    audioUrl: "https://pub-d54bc428c5344e09b63305c4532156ab.r2.dev/Meditation%20Music%2C%20Sleep%20Music%2C%20Relaxing%20Flute%20Music%2C%20Calming%20Music%2C%20KUSALA%2C%20Gus%20Teja%2C%20Bali.mp3",
    slides: [
      "Find a comfortable position. Soften your shoulders.",
      "Breathe in slowly for 4 counts.",
      "Hold gently for 4 counts.",
      "Breathe out fully for 6 counts.",
      "Notice how your body feels right now.",
      "Repeat this breath two more times.",
      "You are here. That is enough."
    ]
  },
  "morning-clarity": {
    title: "Morning Clarity",
    category: "shorts",
    duration: "3 min",
    audioUrl: "https://pub-d54bc428c5344e09b63305c4532156ab.r2.dev/Relaxing%20Pan%20Flute%20Music%20-%20Calming%20Sea.mp3",
    slides: [
      "Good morning. Take a moment before the day begins.",
      "Place your feet on the floor. Feel the ground beneath you.",
      "Take three slow breaths. In... and out.",
      "Set one intention for today. Just one.",
      "What matters most to you right now?",
      "Carry this intention with you today.",
      "You are ready. Begin."
    ]
  },
  "sleep-story": {
    title: "Sleep Journey",
    category: "sleep",
    duration: "10 min",
    audioUrl: "https://pub-d54bc428c5344e09b63305c4532156ab.r2.dev/Relaxing%20Pan%20Flute%20Music%20-%20Calming%20Sea.mp3",
    slides: [
      "You don't need to do anything right now.",
      "Let your body become heavy.",
      "Breathe in... and let go.",
      "Your thoughts are clouds. Let them pass.",
      "Soften your jaw. Soften your eyes.",
      "You are safe. You are still. You can rest.",
      "Let sleep come. You've done enough today."
    ]
  },
  "loving-kindness": {
    title: "Loving Kindness",
    category: "mindfulness",
    duration: "7 min",
    audioUrl: "https://pub-d54bc428c5344e09b63305c4532156ab.r2.dev/Indian%20Background%20Flute%20Music-%20Instrumental%20Meditation%20Music%20-%20Yoga%20Music%20-%20Spa%20Music%20for%20Relaxation.mp3",
    slides: [
      "Place one hand on your heart.",
      "Say quietly: May I be well.",
      "May I be at peace.",
      "May I be kind to myself today.",
      "Think of someone you love. Send them the same wish.",
      "May you be well. May you be at peace.",
      "Now extend it outward. May all of us find our way."
    ]
  },
  "present-moment": {
    title: "Present Moment",
    category: "mindfulness",
    duration: "5 min",
    audioUrl: "https://pub-d54bc428c5344e09b63305c4532156ab.r2.dev/Meditation%20Music%2C%20Sleep%20Music%2C%20Relaxing%20Flute%20Music%2C%20Calming%20Music%2C%20KUSALA%2C%20Gus%20Teja%2C%20Bali.mp3",
    slides: [
      "Pause. You are here, in this moment.",
      "Notice five things you can see around you.",
      "Notice four things you can feel.",
      "Notice three things you can hear.",
      "Notice two things you can smell.",
      "Notice one thing you can taste.",
      "You are present. You are aware. You are enough."
    ]
  },
  "body-scan": {
    title: "Body Scan",
    category: "sleep",
    duration: "8 min",
    audioUrl: "https://pub-d54bc428c5344e09b63305c4532156ab.r2.dev/35433346-angelic-meditation-172334.mp3",
    slides: [
      "Lie down or sit comfortably.",
      "Bring your attention to the top of your head.",
      "Slowly move your awareness down. Forehead. Jaw. Neck.",
      "Shoulders. Arms. Hands. Release any tension you find.",
      "Chest. Belly. Notice your breath here.",
      "Hips. Legs. Feet. Feel the ground beneath you.",
      "Your whole body is here. Present. Resting. Whole."
    ]
  }
};

// Default guided text for sessions without specific content
const DEFAULT_GUIDED_SLIDES = [
  "Find a quiet, comfortable place to sit or lie down.",
  "Close your eyes gently. Take a deep breath in.",
  "Let your shoulders drop. Release any tension.",
  "Breathe naturally. Notice the rhythm of your breath.",
  "Allow your thoughts to come and go like clouds.",
  "Stay here for a moment. You are exactly where you need to be.",
  "When you're ready, slowly open your eyes."
];

// Meditation content data
const MEDITATION_CATEGORIES = {
  shorts: {
    id: "shorts",
    title: "Quick Sessions",
    subtitle: "2-5 minute resets",
    icon: Clock,
    color: "#a69373",
    sessions: [
      {
        id: "breath-reset",
        title: "Breath Reset",
        duration: "3 min",
        description: "Quick breathing exercise to reset your nervous system",
        bgGradient: "from-[#2a3f3a] to-[#1a2f2a]"
      },
      {
        id: "morning-clarity",
        title: "Morning Clarity",
        duration: "3 min",
        description: "Start your day with focused intention",
        bgGradient: "from-[#3a2f2a] to-[#2a1f1a]"
      },
      {
        id: "stress-release",
        title: "Stress Release",
        duration: "4 min",
        description: "Let go of tension in body and mind",
        bgGradient: "from-[#2a2f3a] to-[#1a1f2a]"
      }
    ]
  },
  sleep: {
    id: "sleep",
    title: "Sleep",
    subtitle: "Wind down & rest deeply",
    icon: Moon,
    color: "#7a8aaa",
    sessions: [
      {
        id: "sleep-story",
        title: "Sleep Journey",
        duration: "10 min",
        description: "Gentle guidance into deep, restful sleep",
        bgGradient: "from-[#1a2535] to-[#0a1525]"
      },
      {
        id: "body-scan",
        title: "Body Scan",
        duration: "8 min",
        description: "Progressive relaxation from head to toe",
        bgGradient: "from-[#252535] to-[#151525]"
      },
      {
        id: "night-wind-down",
        title: "Night Wind Down",
        duration: "15 min",
        description: "Soothing guidance for deep sleep",
        bgGradient: "from-[#1a2530] to-[#0a1520]"
      }
    ]
  },
  mindfulness: {
    id: "mindfulness",
    title: "Mindfulness",
    subtitle: "Present moment awareness",
    icon: Sparkles,
    color: "#8a9a7a",
    sessions: [
      {
        id: "present-moment",
        title: "Present Moment",
        duration: "5 min",
        description: "Anchor yourself in the here and now",
        bgGradient: "from-[#2a3a2a] to-[#1a2a1a]"
      },
      {
        id: "loving-kindness",
        title: "Loving Kindness",
        duration: "7 min",
        description: "Cultivate compassion for yourself and others",
        bgGradient: "from-[#3a2a3a] to-[#2a1a2a]"
      },
      {
        id: "gratitude-meditation",
        title: "Gratitude Practice",
        duration: "6 min",
        description: "Connect with appreciation and abundance",
        bgGradient: "from-[#2a3530] to-[#1a2520]"
      }
    ]
  }
};

// Session Card Component
export function SessionCard({ session, category, onPlay, isLocked = false }) {
  const handleClick = () => {
    if (isLocked) {
      window.open(MEMBERSHIP_URL, '_blank', 'noopener,noreferrer');
      return;
    }
    onPlay(session, category);
  };
  
  return (
    <Card 
      className={`overflow-hidden cursor-pointer group hover:shadow-lg transition-all bg-gradient-to-br ${session.bgGradient} border-white/10 ${isLocked ? 'opacity-70' : ''}`}
      onClick={handleClick}
      data-testid={`session-${session.id}`}
    >
      <CardContent className="p-4">
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1 min-w-0">
            <p className="text-white/50 text-xs mb-1">{session.duration}</p>
            <h3 className="text-white font-medium text-base mb-1 truncate">{session.title}</h3>
            <p className="text-white/60 text-sm line-clamp-2">
              {isLocked ? "Upgrade to unlock" : session.description}
            </p>
          </div>
          <button
            onClick={(e) => { e.stopPropagation(); handleClick(); }}
            className={`w-12 h-12 rounded-full flex items-center justify-center transition-colors shrink-0 ${
              isLocked ? 'bg-white/5 hover:bg-white/10' : 'bg-white/10 hover:bg-white/20'
            }`}
          >
            {isLocked ? (
              <Lock className="w-5 h-5 text-white/50" />
            ) : (
              <Play className="w-6 h-6 text-white ml-0.5" />
            )}
          </button>
        </div>
      </CardContent>
    </Card>
  );
}

// Category Section Component
export function CategorySection({ category, onPlay, onViewAll, hasFullAccess = false }) {
  const CategoryIcon = category.icon;
  
  return (
    <section className="mb-6">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <div 
            className="w-8 h-8 rounded-lg flex items-center justify-center"
            style={{ backgroundColor: `${category.color}20` }}
          >
            <CategoryIcon className="w-4 h-4" style={{ color: category.color }} />
          </div>
          <div>
            <h2 className="font-heading text-white text-base">{category.title}</h2>
            <p className="text-white/50 text-xs">{category.subtitle}</p>
          </div>
        </div>
        <button 
          onClick={() => onViewAll(category.id)}
          className="text-[#a69373] text-sm flex items-center gap-1 hover:text-[#d4b47a]"
        >
          View All
          <ChevronRight className="w-4 h-4" />
        </button>
      </div>
      <div className="space-y-3">
        {category.sessions.slice(0, 2).map(session => {
          // Free users can only access breath-reset
          const isLocked = !hasFullAccess && !FREE_MEDITATION_IDS.includes(session.id);
          return (
            <SessionCard 
              key={session.id} 
              session={session} 
              category={category}
              onPlay={onPlay}
              isLocked={isLocked}
            />
          );
        })}
      </div>
    </section>
  );
}

// Full Screen Guided Text Player Component with Background Audio
export function GuidedTextPlayer({ session, category, onClose }) {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [audioLoaded, setAudioLoaded] = useState(false);
  const [isComplete, setIsComplete] = useState(false);
  const timerRef = useRef(null);
  
  // Get slides and audio for this session
  const guidedContent = GUIDED_SESSIONS[session.id] || { slides: DEFAULT_GUIDED_SLIDES };
  const slides = guidedContent.slides;
  const audioUrl = guidedContent.audioUrl;
  const totalSlides = slides.length;
  const progress = ((currentSlide + 1) / totalSlides) * 100;

  // Fire confetti when meditation completes (reaches last slide)
  useEffect(() => {
    if (currentSlide === totalSlides - 1 && !isComplete) {
      setIsComplete(true);
      // Delay confetti slightly for effect
      setTimeout(() => {
        fireConfetti('meditation');
        toast.success("Meditation complete", { 
          description: "Take a moment to notice how you feel.",
          duration: 4000 
        });
      }, 500);
    }
  }, [currentSlide, totalSlides, isComplete]);

  // Start background audio when session opens
  useEffect(() => {
    let errorTimeout = null;
    let audioStarted = false;
    
    if (audioUrl) {
      const startAudio = async () => {
        const audio = await audioManager.play(audioUrl, {
          loop: true,
          volume: 0.08, // 8% volume for background
          source: `meditation-${session.id}`
        });
        
        if (audio) {
          audioStarted = true;
          setAudioLoaded(true);
        }
      };
      
      startAudio();
      
      // Only show error toast if audio hasn't started after 10 seconds
      errorTimeout = setTimeout(() => {
        if (!audioStarted && !audioManager.isPlaying()) {
          console.error('Meditation audio timeout - failed to load within 10s');
          toast.error("Audio unavailable — check your connection.", { duration: 3000 });
        }
      }, 10000);
    }

    // Cleanup on unmount - stop audio and clear timeout
    return () => {
      if (errorTimeout) clearTimeout(errorTimeout);
      audioManager.stop();
    };
  }, [audioUrl, session.id]);

  // Auto-advance slides
  useEffect(() => {
    if (!isPaused && currentSlide < totalSlides - 1) {
      timerRef.current = setTimeout(() => {
        setCurrentSlide(prev => prev + 1);
      }, 8000); // 8 seconds per slide
    }
    return () => clearTimeout(timerRef.current);
  }, [currentSlide, isPaused, totalSlides]);

  const handleNext = () => {
    if (currentSlide < totalSlides - 1) {
      setCurrentSlide(prev => prev + 1);
    }
  };

  const handlePrev = () => {
    if (currentSlide > 0) {
      setCurrentSlide(prev => prev - 1);
    }
  };

  const togglePause = async () => {
    if (isPaused) {
      setIsPaused(false);
      await audioManager.resume();
    } else {
      setIsPaused(true);
      await audioManager.pause();
    }
  };

  const toggleMute = () => {
    const newMuted = audioManager.toggleMute();
    setIsMuted(newMuted);
  };

  const handleClose = async () => {
    await audioManager.stop();
    onClose();
  };

  const CategoryIcon = category?.icon || Sparkles;

  return (
    <div className={`fixed inset-0 z-50 bg-gradient-to-br ${session.bgGradient} flex flex-col`}>
      {/* Header */}
      <div className="flex items-center justify-between p-4 safe-area-top">
        <button 
          onClick={handleClose}
          className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center"
          data-testid="meditation-close"
        >
          <X className="w-5 h-5 text-white" />
        </button>
        <div 
          className="px-3 py-1 rounded-full flex items-center gap-2"
          style={{ backgroundColor: `${category?.color || '#a69373'}20` }}
        >
          <CategoryIcon className="w-4 h-4" style={{ color: category?.color || '#a69373' }} />
          <span className="text-white/70 text-sm">{category?.title || 'Meditation'}</span>
        </div>
        {/* Mute toggle for background audio */}
        <button 
          onClick={toggleMute}
          className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center"
          data-testid="meditation-mute"
        >
          {isMuted ? (
            <VolumeX className="w-5 h-5 text-white/50" />
          ) : (
            <Volume2 className="w-5 h-5 text-white/70" />
          )}
        </button>
      </div>

      {/* Content */}
      <div className="flex-1 flex flex-col items-center justify-center px-8 py-8">
        {/* Session Info */}
        <h1 className="text-xl font-heading text-[#a69373] mb-2 text-center">{session.title}</h1>
        <p className="text-white/40 text-sm mb-12">{currentSlide + 1} of {totalSlides}</p>

        {/* Guided Text */}
        <div className="max-w-md text-center mb-12 min-h-[120px] flex items-center justify-center">
          <p 
            className="text-2xl sm:text-3xl text-white/90 font-light leading-relaxed animate-fade-in"
            key={currentSlide}
          >
            {slides[currentSlide]}
          </p>
        </div>

        {/* Navigation Arrows */}
        <div className="flex items-center gap-8 mb-8">
          <button 
            onClick={handlePrev}
            disabled={currentSlide === 0}
            className="w-12 h-12 rounded-full bg-white/10 flex items-center justify-center hover:bg-white/20 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
          >
            <ChevronLeft className="w-6 h-6 text-white" />
          </button>
          
          <button 
            onClick={togglePause}
            className="w-16 h-16 rounded-full bg-[#a69373] flex items-center justify-center hover:bg-[#d4b47a] transition-colors shadow-lg"
          >
            {isPaused ? (
              <Play className="w-8 h-8 text-[#0B2821] ml-1" />
            ) : (
              <Pause className="w-8 h-8 text-[#0B2821]" />
            )}
          </button>
          
          <button 
            onClick={handleNext}
            disabled={currentSlide === totalSlides - 1}
            className="w-12 h-12 rounded-full bg-white/10 flex items-center justify-center hover:bg-white/20 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
          >
            <ChevronRight className="w-6 h-6 text-white" />
          </button>
        </div>

        {/* Tap to advance hint */}
        <p className="text-white/30 text-sm">Tap arrows or wait to advance</p>
      </div>

      {/* Progress Bar */}
      <div className="px-4 pb-8 safe-area-bottom">
        <div className="w-full h-1 bg-white/10 rounded-full overflow-hidden">
          <div 
            className="h-full bg-[#a69373] rounded-full transition-all duration-500"
            style={{ width: `${progress}%` }}
          />
        </div>
      </div>
    </div>
  );
}

// Main Meditation Section Component for Wellness Tab
export function MeditationSection() {
  const navigate = useNavigate();
  const [activePlayer, setActivePlayer] = useState(null);
  const { user } = useAuth();
  const { subscription } = useSubscription();
  
  // Check if user has full meditation access (any paid tier) - pass user for admin bypass
  const hasFullAccess = hasAccess(subscription.type, 'monthly', user);

  const handlePlay = (session, category) => {
    setActivePlayer({ session, category });
  };

  const handleViewAll = (categoryId) => {
    navigate(`/meditation/${categoryId}`);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3 mb-4">
        <div className="w-10 h-10 rounded-xl bg-[#a69373]/20 flex items-center justify-center">
          <Wind className="w-5 h-5 text-[#a69373]" />
        </div>
        <div>
          <h2 className="font-heading text-white text-lg">Meditation</h2>
          <p className="text-white/50 text-sm">Guided sessions for peace & clarity</p>
        </div>
      </div>

      {/* Categories */}
      <CategorySection 
        category={MEDITATION_CATEGORIES.shorts} 
        onPlay={handlePlay}
        onViewAll={handleViewAll}
        hasFullAccess={hasFullAccess}
      />
      <CategorySection 
        category={MEDITATION_CATEGORIES.sleep} 
        onPlay={handlePlay}
        onViewAll={handleViewAll}
        hasFullAccess={hasFullAccess}
      />
      <CategorySection 
        category={MEDITATION_CATEGORIES.mindfulness} 
        onPlay={handlePlay}
        onViewAll={handleViewAll}
        hasFullAccess={hasFullAccess}
      />

      {/* Player Modal */}
      {activePlayer && (
        <GuidedTextPlayer 
          session={activePlayer.session}
          category={activePlayer.category}
          onClose={() => setActivePlayer(null)}
        />
      )}
    </div>
  );
}

// Export the categories for use in other components
export { MEDITATION_CATEGORIES };
export default MeditationSection;
